import { useEventConfig } from '@/eventConfig/useEventConfig.ts';
import { translate } from '@/translation/translate.ts';
import { EditDataField } from '@/components/slides/DataSummary/components/EditDataField.tsx';
import moment from 'moment';
import { useSummaryFormField } from '@/components/slides/DataSummary/components/useSummaryFormField.ts';
import { EditSaveButtons } from '@/components/slides/DataSummary/components/EditSaveButtons.tsx';
import { useForm } from '@/form/useForm.ts';
import { useEffect } from 'react';
import { ConfigSlideId } from '@/eventConfig/types.ts';

export const PersonalInfoSummaryFormGB = () => {
  const { onStartEdit, onCancelEdit, onSave, editMode, isValid } = useSummaryFormField({
    slideIds: ['birthdate', 'bmi'],
  });
  const { getFieldValue, onChange } = useForm();
  const defaultUnit = 'metric';
  const unit = getFieldValue('units') ?? defaultUnit;

  const { eventConfig } = useEventConfig();
  const s = eventConfig!.slides;

  const unitIsMetric = getFieldValue('units') === 'metric';

  const birthdateField = s.birthdate.fields.find(({ name }) => name === 'birthdate');
  const birthdateLabel = translate(birthdateField?.label);
  const birthdateValue = moment(getFieldValue('birthdate') as string).format('DD.MM.YYYY');

  const unitField = s.bmi.fields.find(({ name }) => name === 'units');
  const unitLabel = translate(unitField?.label);

  const heightField = s.bmi.fields.find(({ name }) => name === 'height');
  const heightLabel = translate(heightField?.label);

  const weightField = s.bmi.fields.find(({ name }) => name === 'weight');
  const weightLabel = translate(weightField?.label);

  const heightInField = s.bmi.fields.find(({ name }) => name === 'heightIn');
  const heightInLabel = translate(heightInField?.label);

  const heightFtField = s.bmi.fields.find(({ name }) => name === 'heightFt');
  const heightFtLabel = translate(heightFtField?.label);

  const weightStField = s.bmi.fields.find(({ name }) => name === 'weightSt');
  const weightStLabel = translate(weightStField?.label);

  const weightLbField = s.bmi.fields.find(({ name }) => name === 'weightLb');
  const weightLbLabel = translate(weightLbField?.label);

  useEffect(() => {
    // Reset the values in the "other" fields so they dont get transmitted
    const reset = {
      value: { raw: null, normalized: null },
      isValid: true,
      hint: undefined,
      slideId: 'bmi' as ConfigSlideId,
    };
    if (unit === 'metric') {
      onChange('heightFt', reset);
      onChange('heightIn', reset);
      onChange('weightSt', reset);
      onChange('weightLb', reset);
    }
    if (unit === 'imperial') {
      onChange('height', reset);
      onChange('weight', reset);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [unit]);

  return (
    <div className="summary-card" data-cy={'summary-form-personal-info'}>
      <h2>{translate(s.dataSummary.titles?.personalInfo)}</h2>
      <EditSaveButtons
        editMode={editMode}
        onSave={onSave}
        onStartEdit={onStartEdit}
        onCancelEdit={onCancelEdit}
        isValid={isValid}
      />

      <div className="data">
        <div className={'label'}>{birthdateLabel}</div>
        <div className={'value'}>
          {!editMode && birthdateValue}
          {editMode && <EditDataField field={birthdateField!} slideId={'birthdate'} />}
        </div>

        {editMode && (
          <>
            <div className={'label'}>{unitLabel}</div>
            <div className={'value'}>
              <EditDataField field={unitField!} slideId={'bmi'} />
            </div>
          </>
        )}

        {unitIsMetric && (
          <>
            <div className={'label'}>{heightLabel}</div>
            <div className={'value'}>
              {!editMode && getFieldValue('height') + ' cm'}
              {editMode && <EditDataField field={heightField!} slideId={'bmi'} />}
            </div>

            <div className={'label'}>{weightLabel}</div>
            <div className={'value'}>
              {!editMode && getFieldValue('weight') + ' kg'}
              {editMode && <EditDataField field={weightField!} slideId={'bmi'} />}
            </div>
          </>
        )}

        {!unitIsMetric && (
          <>
            <div className={'label'}>{heightFtLabel}</div>
            <div className={'value'}>
              {!editMode && getFieldValue('heightFt') + ' ft'}
              {editMode && <EditDataField field={heightFtField!} slideId={'bmi'} />}
            </div>

            <div className={'label'}>{heightInLabel}</div>
            <div className={'value'}>
              {!editMode && getFieldValue('heightIn') + ' in'}
              {editMode && <EditDataField field={heightInField!} slideId={'bmi'} />}
            </div>

            <div className={'label'}>{weightStLabel}</div>
            <div className={'value'}>
              {!editMode && getFieldValue('weightSt') + ' st'}
              {editMode && <EditDataField field={weightStField!} slideId={'bmi'} />}
            </div>

            <div className={'label'}>{weightLbLabel}</div>
            <div className={'value'}>
              {!editMode && getFieldValue('weightLb') + ' lb'}
              {editMode && <EditDataField field={weightLbField!} slideId={'bmi'} />}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
